<template>
  <CustomModal
    :is-modal-open="isOpen"
    :overlay="'dark'"
    :with-header="true"
    :with-footer="true"
    :with-close-btn="false"
    custom-classes="tw:h-screen tw:md:h-auto tw:w-[100vw] tw:md:max-w-[588px] tw:md:w-[588px] tw:md:rounded-xl tw:rounded-none"
    data-testid="rc-plus-cross-sell-subscription-modal"
    @toggleModal="onClose"
    @onClickAway="onClose">
    <template #modalHeader>
      <h3 class="tw:font-bold tw:text-xl">
        {{ step === 'success' ? $t('subscription_payment_success_header') : $t('subscription_payment_header') }}
      </h3>
    </template>

    <RateConnectPlusCrossSellSubscriptionSuccess v-if="step === 'success'" />

    <div v-else class="tw:flex tw:flex-col tw:gap-y-6">
      <p
        v-html="
          $t('rc_plus_upsell_payment_header', {
            DiscountedPrice: discountedPriceHtml,
            OriginalPrice: originalPriceHtml,
          })
        " />

      <div
        v-if="!defaultPaymentMethod?.expired && isPaymentUpdated"
        class="tw:bg-green-200 tw:text-sm tw:rounded-lg tw:px-5 tw:py-4">
        {{ $t('paymentMethods.edit.success') }}
      </div>
      <CardToExpireInfo
        v-if="cardExpiresSoon && !isPaymentUpdated"
        :cardExpirationDate="cardExpirationDate"
        type="PLUS" />
      <CardExpiredInfo v-if="defaultPaymentMethod?.expired && !isPaymentUpdated" type="PLUS" />
      <WireTransferInfo v-if="defaultPaymentMethod?.type === 'WIRE_TRANSFER'" type="PLUS" />
      <DefaultPaymentMethodCard
        :defaultPaymentMethod="defaultPaymentMethod"
        :cardExpiresSoon="cardExpiresSoon"
        type="PLUS"
        @openPaymentPage="openPaymentPage" />
      <div v-if="!isPriceFetching" class="tw:flex tw:justify-between tw:font-bold">
        <p>{{ $t('business_studio_payment_due') }}</p>
        <p class="tw:text-green-700" data-testid="rc-plus-cross-sell-modal-amount-due">
          {{ formatCurrency(amountDue) }}
        </p>
      </div>
      <SmallDotLoader v-else color="#B4183A" />
      <label
        v-if="!defaultPaymentMethod?.expired"
        class="tw:self-start tw:w-fit tw:inline-flex tw:gap-3 tw:items-start tw:mb-0 tw:cursor-pointer">
        <input
          id="rc-plus-cross-sell-terms"
          v-model="termsAccepted"
          type="checkbox"
          class="tw:h-4 tw:w-4 tw:shrink-0 tw:cursor-pointer"
          :disabled="isSubmitting"
          data-testid="rc-plus-cross-sell-terms-checkbox" />
        <span class="tw:text-sm tw:leading-4 terms" v-html="$t('business_studio_plus_terms_and_conditions_checkbox')" />
      </label>
    </div>

    <template #modalFooter>
      <div v-if="step === 'success'" class="tw:flex tw:flex-col tw:md:flex-row tw:md:justify-end tw:w-full tw:gap-4">
        <MainButton
          class="tw:order-1 tw:md:order-2"
          data-testid="rc-plus-cross-sell-success-close-cta"
          @click="closeModal">
          {{ $t('close') }}
        </MainButton>
        <GhostButton
          class="tw:order-2 tw:md:order-1"
          data-testid="rc-plus-cross-sell-success-studio-plus-cta"
          @click="goToStudioPlus">
          {{ $t('business_studio_plus_redirect_cta') }}
        </GhostButton>
      </div>
      <div v-else class="tw:flex tw:flex-col tw:md:flex-row tw:md:justify-end tw:w-full tw:gap-4">
        <MainButton
          class="tw:order-1 tw:md:order-2"
          data-testid="rc-plus-cross-sell-start-subscription-cta"
          :disabled="!canStartSubscription"
          @click="startSubscription">
          <span v-show="!isSubmitting">{{ $t('business_studio_plus_checkout_cta_button') }}</span>
          <span v-show="isSubmitting" class="tw:px-4"><SmallDotLoader color="#fff" /></span>
        </MainButton>
        <GhostButton class="tw:order-2 tw:md:order-1" :disabled="isSubmitting" @click="onClose">{{
          $t('rc_plus_upsell_back_cta')
        }}</GhostButton>
      </div>
    </template>
  </CustomModal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import CustomModal from '@/components/base/CustomModal.vue'
import DefaultPaymentMethodCard from '@/components/rateConnect/resumeFlow/DefaultPaymentMethodCard.vue'
import CardToExpireInfo from '@/components/rateConnect/resumeFlow/CardToExpireInfo.vue'
import CardExpiredInfo from '@/components/rateConnect/resumeFlow/CardExpiredInfo.vue'
import WireTransferInfo from '@/components/rateConnect/resumeFlow/WireTransferInfo.vue'
import SmallDotLoader from '@/components/base/loaders/SmallDotLoader.vue'
import RateConnectPlusCrossSellSubscriptionSuccess from '@/components/rateConnect/studioPlusCrossSell/RateConnectPlusCrossSellSubscriptionSuccess.vue'
import useToastNotifications from '@/components/notifications/useToastNotifications'
import { useStudioPlusNewSubscriptionMutation } from '@/components/payment/queries'
import { selectedAccommodationId, useAccommodationsByOrgId, useUserDetails } from '@/layouts/queries'
import { useRcPlusCrossSell, mapToBackendSubscriptionSource } from '@/composables/useRcPlusCrossSell'
import { useFormatting } from '@/composables/useFormatting'
import { useSessionValues } from '@/composables/useSessionValues'
import GhostButton from '@/components/base/buttons/GhostButton.vue'
import MainButton from '@/components/base/buttons/MainButton.vue'
import appAnalytics from '@/utils/tracking'

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits<{ close: [dismiss?: boolean] }>()

const router = useRouter()
const { showSaveError } = useToastNotifications()
const { language, userId, selectedOrganisationId } = useSessionValues()
const { defaultPaymentMethod, studioPlusPrice, isPriceFetching, subscriptionTrackingSource } = useRcPlusCrossSell()
const { formatCurrency, formatDate } = useFormatting()
const { userDetails } = useUserDetails(userId)
const { accommodationsData } = useAccommodationsByOrgId(selectedOrganisationId)
const { sendStudioPlusNewSubscription } = useStudioPlusNewSubscriptionMutation()

const step = ref<'checkout' | 'success'>('checkout')
const termsAccepted = ref(false)
const isSubmitting = ref(false)

const amountDue = computed(() => studioPlusPrice.value?.totalPrice ?? 0)
const baseSubscriptionFullTermPrice = computed(() => studioPlusPrice.value?.baseSubscriptionFullTermPrice ?? 0)

const discountedPriceHtml = computed(
  () => `<span class="tw:font-bold tw:text-green-700">${formatCurrency(amountDue.value)}</span>`
)
const originalPriceHtml = computed(
  () => `<span class="tw:line-through">${formatCurrency(baseSubscriptionFullTermPrice.value)}</span>`
)

const isPaymentUpdated = computed(() => router.currentRoute.value.query?.isUpdated === 'true')

const cardExpirationDate = computed(() => {
  const paymentMethod = defaultPaymentMethod.value
  if (!paymentMethod || paymentMethod.type !== 'CREDIT_CARD') {
    return null
  }
  if (!paymentMethod.expirationMonth || !paymentMethod.expirationYear) {
    return null
  }
  return new Date(paymentMethod.expirationYear, paymentMethod.expirationMonth, 0)
})

const cardExpiresSoon = computed(() => {
  if (!cardExpirationDate.value) {
    return false
  }
  const today = new Date()
  const msPerDay = 24 * 60 * 60 * 1000
  const daysUntilExpiration = Math.ceil((cardExpirationDate.value.getTime() - today.getTime()) / msPerDay)
  return daysUntilExpiration < 30 && daysUntilExpiration >= 0
})

const accommodationName = computed(
  () =>
    accommodationsData.value?.find(item => Number(item.accommodationId) === Number(selectedAccommodationId.value))?.name
)

const canStartSubscription = computed(
  () =>
    termsAccepted.value &&
    !isSubmitting.value &&
    !isPriceFetching.value &&
    !!studioPlusPrice.value &&
    !!defaultPaymentMethod.value &&
    !defaultPaymentMethod.value?.expired &&
    defaultPaymentMethod.value?.type !== 'WIRE_TRANSFER'
)

const resetModalState = () => {
  step.value = 'checkout'
  termsAccepted.value = false
  isSubmitting.value = false
}

watch(
  () => props.isOpen,
  isOpen => {
    if (!isOpen) resetModalState()
  }
)

const closeModal = () => {
  emit('close')
}

const onClose = () => {
  if (isSubmitting.value) return
  if (step.value === 'success') {
    closeModal()
    return
  }
  emit('close', true)
}

const openPaymentPage = () => {
  router.push({
    name: 'billing-edit-payment',
    query: {
      isResumeFlow: 'true',
      sourceRoute: String('rate-connect-running-performance'),
      productType: 'RC_PLUS_CROSS_SELL',
    },
  })
  closeModal()
}

const goToStudioPlus = () => {
  router.push({ name: 'studio-plus-insights' })
  closeModal()
}

const startSubscription = () => {
  isSubmitting.value = true

  sendStudioPlusNewSubscription(
    {
      accommodationId: selectedAccommodationId.value,
      itemName: accommodationName.value,
      email: userDetails?.value?.email,
      language: language?.value,
      studioUserId: userId.value,
      firstName: userDetails?.value?.firstName,
      lastName: userDetails?.value?.lastName,
      subscriptionSource: mapToBackendSubscriptionSource(subscriptionTrackingSource.value),
      ...(studioPlusPrice.value?.itemPriceVoucher?.isValid && {
        voucherCode: studioPlusPrice.value.itemPriceVoucher.voucherCode,
      }),
    },
    {
      onSuccess: data => {
        isSubmitting.value = false
        step.value = 'success'
        appAnalytics.track(appAnalytics.events.SP_PAYMENT_SUCCEED, {
          item_id: selectedAccommodationId.value,
          price: studioPlusPrice.value?.baseSubscriptionMonthlyPrice,
          language: language.value,
          studioUserId: userId.value,
          email: userDetails?.value?.email,
          firstName: userDetails?.value?.firstName,
          lastName: userDetails?.value?.lastName,
          source: subscriptionTrackingSource.value,
          subscription_date: formatDate(data?.data?.subscriptionStartDate, {
            year: 'numeric',
            month: 'long',
            day: '2-digit',
          }),
          renewal_date: formatDate(data?.data?.nextPaymentDate, {
            year: 'numeric',
            month: 'long',
            day: '2-digit',
          }),
        })
      },
      onError: () => {
        isSubmitting.value = false
        showSaveError()
      },
    }
  )
}
</script>

<style scoped>
.terms :deep(a) {
  color: var(--tw-color-blue-700);
  cursor: pointer;
}
</style>
